/**
 * The bars of "Movement by ACRISS" (CU-869fn0gcp phase 2, brief block 6, criterion 18): one bar of yours and one of
 * your tier per ACRISS, in % (the average of each rate's change of `by_acriss`, as the table under it) or in € (the
 * average of each rate's € change, in the page's unit). Your bar takes the BI colour rule by the sign of your change (green ▼ down, red ▲ up, slate inside
 * the ±2 % band — `@utils/colourScale.js`); the tier is grey. The values are the table's, never recomputed beyond the
 * unit (TL C3). Pure and alias-free.
 */
import { tone, pct } from '../../../../@utils/colourScale.js'
import { sideDiffInUnit, signedMoney } from './amounts.js'

export const METRICS = Object.freeze({ PCT: 'pct', EUR: 'eur' })
export const METRIC_OPTIONS = Object.freeze([
	{ value: METRICS.PCT, label: '%' },
	{ value: METRICS.EUR, label: '€' },
])
export const BAR_COLORS = Object.freeze({ down: '#16A34A', up: '#DC2626', flat: '#64748B', tier: '#CBD2DC' })

const isNum = v => typeof v === 'number' && Number.isFinite(v)
const r2 = v => (isNum(v) ? Math.round(v * 100) / 100 + 0 : null)
const r1 = v => (isNum(v) ? Math.round(v * 10) / 10 + 0 : null)

/** The colour of your bar from the sign of your change (the %), whatever the metric shown. */
export function youColor(ratio) {
	const t = tone(ratio)
	if (!t || t.side === 'inline') return BAR_COLORS.flat
	return t.side === 'cheaper' ? BAR_COLORS.down : BAR_COLORS.up
}

/** `−10.4%` or `−16.60 €`, `—` without a figure. */
export function barText(value, metric) {
	if (!isNum(value)) return '—'
	return metric === METRICS.EUR ? signedMoney(value) : pct(value / 100)
}

/**
 * `[{ sipp, you, tier, youText, tierText, color }]` in the server's order, `you`/`tier` in percent points of change
 * (`−10.4`) or euros of change in the unit (`−16.6`); null when the side has no figure. ACRISS without any figure
 * are left out (nothing to draw).
 */
export function movementBars(data, { metric = METRICS.PCT, unit = 'total' } = {}) {
	const lor = data?.sample?.lor || 1
	return (data?.by_acriss || []).map(r => {
		const you = metric === METRICS.EUR ? r2(sideDiffInUnit(r.own, unit, lor)) : r1(isNum(r.own?.change) ? r.own.change * 100 : null)
		const tier = metric === METRICS.EUR ? r2(sideDiffInUnit(r.market, unit, lor)) : r1(isNum(r.market?.change) ? r.market.change * 100 : null)
		return { sipp: r.sipp, you, tier, youText: barText(you, metric), tierText: barText(tier, metric), color: youColor(r.own?.change) }
	}).filter(b => b.you !== null || b.tier !== null)
}

/** What a screen reader hears for the chart: one sentence per ACRISS. */
export function barsLabel(bars, metric) {
	if (!bars?.length) return 'No ACRISS with a change to draw.'
	const unit = metric === METRICS.EUR ? 'in euros' : 'in percent'
	return `Change by ACRISS, you vs your tier, ${unit}. ${bars.map(b => `${b.sipp}: you ${b.youText}, tier ${b.tierText}`).join('; ')}.`
}
